
import { CommonServiceIds, CompositeDisposable, Control, IComponent, IDesigner, IDesignerHost, ISelectionService } from '@genexus/designer-common';
import { Component, Element, Listen, Prop, State, h, Host } from '@stencil/core';

@Component({
  tag: 'gx-designer-view',
  styleUrl: 'designer-view.scss',
  shadow: false,
})
export class DesignerView {

  @Element() el: HTMLElement;

  @Prop() control: Control;
  @Prop() changeSmooth: boolean;
  @Prop() toolbarHidden: boolean

  @State() private _state?: { selected: boolean, bounds: DOMRect };
  private _resizeObserver: ResizeObserver;
  private _disposables: CompositeDisposable;
  private _surface: HTMLDivElement;
  private _dispatchedEvents: WeakSet<Event>;

  constructor() {
    this._disposables = new CompositeDisposable();
    this._dispatchedEvents = new WeakSet();
    this._resizeObserver = new window.ResizeObserver(
      () => this._updateState()
    );
  }

  render() {
    return (
      <Host>
        <div style={this._getStyle()} class={this._getClasses()}>
          {!this.toolbarHidden && this._getSelectionService()?.primary === this.control && (
            <gx-designer-tool-bar
              class="location-outside-top"
              data-gx-designer-tool-arrange-locations="location-outside-top location-outside-right location-outside-bottom location-outside-left location-inside-sticky location-viewport-top"
              control={this.control}
              onSelectControl={(event) => this._selectControl(event)}
            />
          )}
          <div class="surface" ref={(e) => this._surface = e}>
          </div>
          <slot />
        </div>
      </Host>
    );
  }

  componentDidLoad() {
    let designer = this._getDesigner();
    designer?.initializeSurface(this._surface);
  }

  componentWillLoad() {
    this._updateState();
  }

  componentWillRender() {
    this._updateState();
  }

  connectedCallback() {
    this._subscribeToDesignerEvents();
    if (this.control)
      this._resizeObserver.observe(this.control);
  }

  disconnectedCallback() {
    let designer = this._getDesigner();
    designer?.onWillDestroySurface();
    this._disposables.dispose();
    this._resizeObserver.unobserve(this.control);
  }

  private _subscribeToDesignerEvents() {
    this._disposables = new CompositeDisposable();
  }

  @Listen("scroll", { target: "window", passive: true })
  @Listen("resize", { target: "window", passive: true })
  @Listen("dragstart", { target: "window", passive: true })
  @Listen("dragenter", { target: "window", passive: true })
  @Listen("dragend", { target: "window", passive: true })
  private _handleWindowEvents() {
    this._updateState();
  }

  @Listen("dragover", { passive: false })
  private _onDragOver(e: DragEvent) {
    if (!this._dispatchedEvents.has(e)) {
      //f (!this._isDescendant(this._surface, e.target as HTMLElement)) {

      e.preventDefault();
      e.stopPropagation();

      let target = this._elementFromPointWithin(this._surface, e.clientX, e.clientY);
      if (target) {
        let evt = new DragEvent('dragover', { relatedTarget: target, clientX: e.clientX, clientY: e.clientY, bubbles: true });
        this._dispatchedEvents.add(evt);
        target.dispatchEvent(evt);
      }
    }
  }

  @Listen("drop", { passive: false })
  private _onDrop(e: DragEvent) {
    if (!this._dispatchedEvents.has(e)) {
      e.preventDefault();
      e.stopPropagation();

      let target = this._elementFromPointWithin(this._surface, e.clientX, e.clientY);
      if (target) {
        let evt = new DragEvent('drop', { relatedTarget: target, clientX: e.clientX, clientY: e.clientY, bubbles: true });
        this._dispatchedEvents.add(evt);
        target.dispatchEvent(evt);
      }
    }
  }

  private _updateState() {
    if (this.control) {
      this._state = {
        bounds: this.control.getBoundingClientRect(),
        selected: !!this._getSelectionService()?.isSelected(this.control)
      }
    }
    else
      this._state = undefined;
  }

  private _getStyle() {
    return this._state
      ? {
        top: `${this._state.bounds.top}px`,
        left: `${this._state.bounds.left}px`,
        width: `${this._state.bounds.width}px`,
        height: `${this._state.bounds.height}px`,

        // workaround to bug of chromium with position:sticky
        '--sticky-top': `${this._state.bounds.top >= 0 ? 0 : this._state.bounds.top * -1}px`
      }
      : {};
  }

  private _getClasses() {
    if (this._state?.selected)
      if (this.control === this._getSelectionService()?.last)
        return 'root selected highlight';
      else
        return 'root selected';

    return 'root';
  }

  private _smooth() {
    if (this.changeSmooth) {
      this.el.classList.add("smooth");
    }
  }

  private _smoothSuspend() {
    if (this.changeSmooth) {
      this.el.classList.remove("smooth");
    }
  }

  private _smoothResume() {
    if (this.changeSmooth) {
      window.requestAnimationFrame(() => {
        this.el.classList.add("smooth");
      });
    }
  }

  private _getSelectionService(): ISelectionService {
    return this.control?.site.getService(CommonServiceIds.Selection) as ISelectionService;
  }

  private _getHost(): IDesignerHost {
    return this.control?.site.getService(CommonServiceIds.DesignerHost) as IDesignerHost;
  }

  private _getDesigner(): IDesigner {
    return this._getHost()?.getDesigner(this.control);
  }

  private _elementFromPointWithin(container: Element, x: number, y: number): Element | null {
    const elements = document.elementsFromPoint(x, y);
    for (const element of elements) {
      if (container.contains(element)) {
        return element;
      }
    }

    return null;
  }

  private _isDescendant(parent: Element, child: Element): boolean {
    if (parent === child)
      return true;

    let currentNode: Node | null = child?.parentNode;

    while (currentNode !== null) {
      if (currentNode === parent) {
        return true;
      }

      currentNode = currentNode.parentNode;
    }

    return false;
  }

  private _selectControl(event:CustomEvent):void{
    let component:IComponent = this._getHost().components.find(c => c.site.id === event.detail.controlId) as IComponent;
    if (component){
      let selectionService = this._getSelectionService();
      selectionService.setSelection([ component ]);
    }    
  }
}

